import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Collapse,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, parseISO, subHours } from 'date-fns';
import {
  LineChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  YAxis,
} from 'recharts';
import { treatmentSheetApi } from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import { treatmentSheetControls } from '../../auth/pageControls';
import type {
  VitalEntry,
  LogVitalsPayload,
  MMColor,
} from '../../types/treatmentSheet';

// ─── Reference ranges ─────────────────────────────────────────────────────────

const HR_MIN = 28;
const HR_MAX = 44;
const RR_MIN = 8;
const RR_MAX = 16;
const TEMP_MIN = 99.0;
const TEMP_MAX = 101.5;

// ─── MM Color display ─────────────────────────────────────────────────────────

const MM_COLOR_MAP: Record<MMColor, { dot: string; label: string }> = {
  PINK: { dot: '#4caf50', label: 'Pink' },
  PALE_PINK: { dot: '#cddc39', label: 'Pale Pink' },
  PALE: { dot: '#ffeb3b', label: 'Pale' },
  WHITE: { dot: '#eeeeee', label: 'White' },
  TACKY: { dot: '#ff9800', label: 'Tacky' },
  INJECTED: { dot: '#f44336', label: 'Injected' },
  CYANOTIC: { dot: '#b71c1c', label: 'Cyanotic' },
  ICTERIC: { dot: '#ffc107', label: 'Icteric' },
};

// ─── Sparkline chart ──────────────────────────────────────────────────────────

interface SparklineProps {
  data: Array<{ time: string; value: number }>;
  refMin?: number;
  refMax?: number;
  color?: string;
  unit?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  refMin,
  refMax,
  color = '#1976d2',
  unit = '',
}) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={100}>
      <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -30 }}>
        <YAxis tick={{ fontSize: 9 }} />
        <RechartsTooltip
          formatter={(val: number) => [`${val}${unit}`, '']}
          labelFormatter={(label) => {
            try {
              return format(parseISO(label), 'h:mm a');
            } catch {
              return label;
            }
          }}
          contentStyle={{ fontSize: 11 }}
        />
        {refMin !== undefined && (
          <ReferenceLine
            y={refMin}
            stroke={theme.palette.grey[400]}
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        )}
        {refMax !== undefined && (
          <ReferenceLine
            y={refMax}
            stroke={theme.palette.grey[400]}
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={{ r: 2 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ─── Vital value display ──────────────────────────────────────────────────────

interface VitalValueProps {
  label: string;
  value?: number | string;
  unit?: string;
  isAbnormal?: boolean;
}

const VitalValue: React.FC<VitalValueProps> = ({ label, value, unit, isAbnormal }) => {
  const theme = useTheme();
  const hasValue = value !== undefined && value !== null;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        color={isAbnormal ? theme.palette.error.main : 'text.primary'}
        sx={{ lineHeight: 1.2 }}
      >
        {hasValue ? `${value}${unit ?? ''}` : '—'}
      </Typography>
    </Box>
  );
};

// ─── Log Vitals Form ──────────────────────────────────────────────────────────

interface LogVitalsFormProps {
  sheetId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const LogVitalsForm: React.FC<LogVitalsFormProps> = ({ sheetId, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<LogVitalsPayload>({});
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof LogVitalsPayload>(key: K, value: LogVitalsPayload[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const mutation = useMutation(
    () => treatmentSheetApi.logVitals(sheetId, form),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vitals', sheetId]);
        onSuccess();
      },
      onError: () => setError('Failed to save vitals. Please try again.'),
    },
  );

  return (
    <Box sx={{ mt: 1, mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Log Vitals
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <TextField
            label="Heart Rate (bpm)"
            type="number"
            size="small"
            fullWidth
            value={form.heartRate ?? ''}
            onChange={(e) => set('heartRate', e.target.value ? Number(e.target.value) : undefined)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Resp. Rate (brpm)"
            type="number"
            size="small"
            fullWidth
            value={form.respiratoryRate ?? ''}
            onChange={(e) => set('respiratoryRate', e.target.value ? Number(e.target.value) : undefined)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Temperature (°F)"
            type="number"
            size="small"
            fullWidth
            value={form.temperatureF ?? ''}
            onChange={(e) => set('temperatureF', e.target.value ? Number(e.target.value) : undefined)}
            inputProps={{ step: 0.1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="CRT (seconds)"
            type="number"
            size="small"
            fullWidth
            value={form.crtSeconds ?? ''}
            onChange={(e) => set('crtSeconds', e.target.value ? Number(e.target.value) : undefined)}
            inputProps={{ step: 0.5 }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl size="small" fullWidth>
            <InputLabel>MM Color</InputLabel>
            <Select
              label="MM Color"
              value={form.mmColor ?? ''}
              onChange={(e) => set('mmColor', (e.target.value as MMColor) || undefined)}
            >
              <MenuItem value="">(none)</MenuItem>
              {(Object.keys(MM_COLOR_MAP) as MMColor[]).map((k) => (
                <MenuItem key={k} value={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: MM_COLOR_MAP[k].dot,
                      border: '1px solid rgba(0,0,0,.12)',
                      flexShrink: 0,
                    }}
                  />
                  {MM_COLOR_MAP[k].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Gut Sounds"
            size="small"
            fullWidth
            value={form.gutSounds ?? ''}
            onChange={(e) => set('gutSounds', e.target.value || undefined)}
            placeholder="e.g. Normal all 4 quadrants; reduced on right"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Pain Score: {form.painScore ?? 0}
          </Typography>
          <Slider
            value={form.painScore ?? 0}
            min={0}
            max={5}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_, v) => set('painScore', v as number)}
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Body Weight (lbs)"
            type="number"
            size="small"
            fullWidth
            value={form.bodyWeight ?? ''}
            onChange={(e) => set('bodyWeight', e.target.value ? Number(e.target.value) : undefined)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value || undefined)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button size="small" onClick={onCancel} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isLoading}
          startIcon={mutation.isLoading ? <CircularProgress size={14} /> : undefined}
        >
          Save Vitals
        </Button>
      </Box>
    </Box>
  );
};

// ─── VitalsPanel ──────────────────────────────────────────────────────────────

interface VitalsPanelProps {
  sheetId: string;
}

const VitalsPanel: React.FC<VitalsPanelProps> = ({ sheetId }) => {
  const rbac = useRBAC();
  const can = treatmentSheetControls(rbac);
  const theme = useTheme();
  const [showForm, setShowForm] = useState(false);

  const { data: vitals = [], isLoading, isError } = useQuery<VitalEntry[]>(
    ['vitals', sheetId],
    () => treatmentSheetApi.listVitals(sheetId),
    { refetchInterval: 60000 },
  );

  const latest = vitals.length > 0 ? vitals[0] : null;
  const cutoff = subHours(new Date(), 24);
  const recent = vitals.filter((v) => parseISO(v.recordedAt) >= cutoff);

  const hrData = recent
    .filter((v) => v.heartRate !== undefined)
    .map((v) => ({ time: v.recordedAt, value: v.heartRate! }))
    .reverse();

  const rrData = recent
    .filter((v) => v.respiratoryRate !== undefined)
    .map((v) => ({ time: v.recordedAt, value: v.respiratoryRate! }))
    .reverse();

  const tempData = recent
    .filter((v) => v.temperatureF !== undefined && v.temperatureF !== null)
    .map((v) => ({ time: v.recordedAt, value: parseFloat(String(v.temperatureF)) }))
    .reverse();

  const isHRAbnormal =
    latest?.heartRate !== undefined &&
    (latest.heartRate < HR_MIN || latest.heartRate > HR_MAX);
  const isRRAbnormal =
    latest?.respiratoryRate !== undefined &&
    (latest.respiratoryRate < RR_MIN || latest.respiratoryRate > RR_MAX);
  const isTempAbnormal =
    latest?.temperatureF != null &&
    (parseFloat(String(latest.temperatureF)) < TEMP_MIN || parseFloat(String(latest.temperatureF)) > TEMP_MAX);
  const isPainAbnormal = latest?.painScore !== undefined && latest.painScore >= 3;

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Vitals
        </Typography>
        {/* vital-entry.controller.ts `create` :20-21 → manage:medical_records. The panel's chart
            and history stay: reading a patient's vitals is `view:medical_records`, which this
            screen's route already required to open at all. */}
        {can.vitals && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowForm((p) => !p)}
            variant={showForm ? 'outlined' : 'contained'}
          >
            {showForm ? 'Cancel' : 'Log Vitals'}
          </Button>
        )}
      </Box>

      <Box sx={{ overflow: 'auto', flex: 1, p: 2 }}>
        <Collapse in={showForm}>
          <LogVitalsForm
            sheetId={sheetId}
            onSuccess={() => setShowForm(false)}
            onCancel={() => setShowForm(false)}
          />
        </Collapse>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isError && (
          <Alert severity="error">Failed to load vitals.</Alert>
        )}

        {!isLoading && !isError && (
          <>
            {/* Current values */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Values
              {latest?.recordedAt && (
                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                  — {format(parseISO(latest.recordedAt), 'h:mm a, MMM d')}
                </Typography>
              )}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <VitalValue
                  label="HR"
                  value={latest?.heartRate}
                  unit=" bpm"
                  isAbnormal={isHRAbnormal}
                />
              </Grid>
              <Grid item xs={4}>
                <VitalValue
                  label="RR"
                  value={latest?.respiratoryRate}
                  unit=" brpm"
                  isAbnormal={isRRAbnormal}
                />
              </Grid>
              <Grid item xs={4}>
                <VitalValue
                  label="Temp"
                  value={latest?.temperatureF != null ? parseFloat(String(latest.temperatureF)).toFixed(1) : undefined}
                  unit="°F"
                  isAbnormal={isTempAbnormal}
                />
              </Grid>
              <Grid item xs={4}>
                <VitalValue
                  label="CRT"
                  value={latest?.crtSeconds != null ? parseFloat(String(latest.crtSeconds)) : undefined}
                  unit="s"
                />
              </Grid>
              <Grid item xs={4}>
                <VitalValue
                  label="Pain"
                  value={latest?.painScore}
                  unit="/5"
                  isAbnormal={isPainAbnormal}
                />
              </Grid>
              <Grid item xs={4}>
                {/* MM Color */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    MM Color
                  </Typography>
                  {latest?.mmColor ? (
                    <Tooltip title={MM_COLOR_MAP[latest.mmColor]?.label ?? latest.mmColor}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: MM_COLOR_MAP[latest.mmColor]?.dot ?? '#ccc',
                          border: '2px solid rgba(0,0,0,.15)',
                          mx: 'auto',
                          mt: 0.5,
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                      —
                    </Typography>
                  )}
                </Box>
              </Grid>
              {latest?.gutSounds && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Gut Sounds: <strong>{latest.gutSounds}</strong>
                  </Typography>
                </Grid>
              )}
              {latest?.bodyWeight && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Body Weight: <strong>{parseFloat(String(latest.bodyWeight))} lbs</strong>
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Sparklines */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Last 24 Hours
            </Typography>

            {hrData.length > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Heart Rate (bpm) — normal {HR_MIN}–{HR_MAX}
                </Typography>
                <Sparkline
                  data={hrData}
                  refMin={HR_MIN}
                  refMax={HR_MAX}
                  color={theme.palette.primary.main}
                  unit=" bpm"
                />
              </Box>
            )}
            {rrData.length > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Resp. Rate (brpm) — normal {RR_MIN}–{RR_MAX}
                </Typography>
                <Sparkline
                  data={rrData}
                  refMin={RR_MIN}
                  refMax={RR_MAX}
                  color={theme.palette.secondary.main}
                  unit=" brpm"
                />
              </Box>
            )}
            {tempData.length > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Temperature (°F) — normal {TEMP_MIN}–{TEMP_MAX}
                </Typography>
                <Sparkline
                  data={tempData}
                  refMin={TEMP_MIN}
                  refMax={TEMP_MAX}
                  color={theme.palette.warning.main}
                  unit="°F"
                />
              </Box>
            )}

            {vitals.length === 0 && !showForm && (
              <Typography variant="body2" color="text.disabled" textAlign="center" mt={2}>
                No vitals recorded yet.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default VitalsPanel;
